/**
 * Finance Module Types
 * Types for license management, usage tracking, and billing
 */

export interface LicenseTier {
  id: string
  name: string
  yearlyEvents: number
  monthlyAverage: number
  pricePerYear: number
  overageRate: number // per 1000 events
}

export interface AccountLicense {
  accId: number
  accountName: string
  tier: LicenseTier
  contractStartDate: string
  contractEndDate: string
  yearlyLimit: number
  monthlyAverage: number
}

export interface MonthlyUsage {
  month: string // YYYY-MM
  dataIn: number
  dataOut: number
  totalEvents: number
}

export interface AccountUsageData {
  accId: number
  accountName: string
  license: AccountLicense
  yearToDateUsage: number
  currentMonthUsage: number
  monthlyUsage: MonthlyUsage[]
  utilizationPercent: number // YTD usage / (yearly limit * elapsed months / 12)
  projectedYearlyUsage: number
  projectedOverage: number
  status: 'healthy' | 'warning' | 'critical' | 'underutilized'
  riskScore: number // 0-100
  monthsRemaining: number
  averageMonthlyUsage: number
}

export interface UsageSummary {
  totalAccounts: number
  totalYearlyLicensedEvents: number
  totalYtdUsage: number
  totalProjectedUsage: number
  accountsAtRisk: number
  accountsUnderutilized: number
  potentialOverageRevenue: number
  potentialChurnRisk: number // revenue from underutilized accounts
}

export type UsageStatus = 'healthy' | 'warning' | 'critical' | 'underutilized'

export interface StatusConfig {
  label: string
  color: string
  bgColor: string
  borderColor: string
  description: string
}

export const STATUS_CONFIG: Record<UsageStatus, StatusConfig> = {
  healthy: {
    label: 'Healthy',
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    description: 'Usage is within expected range',
  },
  warning: {
    label: 'Warning',
    color: 'text-amber-700',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    description: 'Projected to exceed 90% of license',
  },
  critical: {
    label: 'Overage Risk',
    color: 'text-red-700',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    description: 'Projected to exceed license limit',
  },
  underutilized: {
    label: 'Underutilized',
    color: 'text-blue-700',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    description: 'Using less than 50% of license',
  },
}
